import { parseBackendDate } from '@/utils/date_utils';
import { dataProviderWithCustomMethod } from '@/providers/loginWithEmail/dataProvider';
import { draftFromLegacyEstimationData } from './uiDraft';

export type ProjectQuoteListRow = { id: number; created_at: string };

/**
 * Sort project quotes by created_at ascending (oldest first),
 * with ties broken by smallest id.
 * Handles unparseable dates by sorting them last (NaN maps to +Infinity).
 * Non-array / empty input → [].
 */
export function sortProjectQuotesOldestFirst(
  rows: ProjectQuoteListRow[] | null | undefined,
): ProjectQuoteListRow[] {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const sorted = [...rows].sort((a, b) => {
    const timeA = parseBackendDate(a.created_at).getTime();
    const timeB = parseBackendDate(b.created_at).getTime();

    const normA = Number.isNaN(timeA) ? Number.POSITIVE_INFINITY : timeA;
    const normB = Number.isNaN(timeB) ? Number.POSITIVE_INFINITY : timeB;

    if (normA !== normB) {
      return normA - normB;
    }

    // Tie-break by id asc
    return a.id - b.id;
  });

  return sorted;
}

/**
 * Pick the first (oldest) quote from a list sorted by created_at.
 * Returns null if the list is empty or not an array.
 */
export function pickFirstProjectQuote(
  rows: ProjectQuoteListRow[] | null | undefined,
): ProjectQuoteListRow | null {
  const sorted = sortProjectQuotesOldestFirst(rows);
  return sorted[0] ?? null;
}

/**
 * Check if an estimation_data object contains a usable estimator snapshot.
 * Returns true if:
 * - ui_draft is a non-empty object, OR
 * - any legacy key is present (client_information, project_context, roof_system, measurements, cost_sheet)
 * Returns false for {legacy_data: …}-only, {}, null, or undefined.
 */
export function hasUsableEstimatorSnapshot(
  ed: Record<string, unknown> | null | undefined,
): boolean {
  if (!ed || typeof ed !== 'object') {
    return false;
  }

  const edObj = ed as Record<string, unknown>;

  // Check for ui_draft
  const uiDraft = edObj.ui_draft;
  if (uiDraft && typeof uiDraft === 'object' && Object.keys(uiDraft).length > 0) {
    return true;
  }

  // Check for legacy keys
  const legacyKeys = [
    'client_information',
    'project_context',
    'roof_system',
    'measurements',
    'cost_sheet',
  ];

  for (const key of legacyKeys) {
    if (key in edObj) {
      return true;
    }
  }

  return false;
}

/**
 * Keys that should be blanked (set to []) when copying a draft from an existing quote.
 * File references must not cross quotes to avoid re-binding another quote's ProjectFile rows.
 */
export const PREFILL_BLANKED_DRAFT_KEYS = [
  'attachments',
  'relatedQuoteFiles',
  'viewFiles',
] as const;

/**
 * Mapping of draft keys to estimation_data top-level keys for fallback values.
 * Used when the draft key is missing or empty (to support old ui_draft rows
 * that predate these keys in the draft structure).
 */
export const PREFILL_TOP_LEVEL_FALLBACKS: Record<
  string,
  string
> = {
  scopeHtml: 'scope_html',
  woodHtml: 'wood_html',
  permitsHtml: 'permits_html',
  permitsHidden: 'permits_hidden',
  completionDays: 'completion_days',
  architectProject: 'architect_project',
};

/**
 * Extract and prepare a draft from an existing quote's estimation_data.
 * Returns null if the snapshot is not usable.
 *
 * Process:
 * 1. Check if the snapshot is usable; return null if not.
 * 2. Extract ui_draft if present; otherwise convert from legacy via draftFromLegacyEstimationData.
 * 3. Blank file reference keys (attachments, relatedQuoteFiles, viewFiles) → []
 * 4. Drop project identity keys (selectedProjectId, selectedProjectNo)
 * 5. Fold in top-level estimation_data fallbacks for scope_html/wood_html/permits_html/
 *    permits_hidden/completion_days/architect_project (only when the draft key is missing/empty)
 */
export function draftFromExistingQuoteSnapshot(
  ed: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!hasUsableEstimatorSnapshot(ed)) {
    return null;
  }

  const edObj = ed as Record<string, unknown>;

  let draft: Record<string, unknown>;

  // Extract or convert ui_draft
  if (
    edObj.ui_draft &&
    typeof edObj.ui_draft === 'object' &&
    Object.keys(edObj.ui_draft).length > 0
  ) {
    draft = { ...edObj.ui_draft };
  } else {
    draft = draftFromLegacyEstimationData(edObj);
  }

  // Blank file reference keys
  for (const key of PREFILL_BLANKED_DRAFT_KEYS) {
    draft[key] = [];
  }

  // Drop project identity keys
  delete draft.selectedProjectId;
  delete draft.selectedProjectNo;

  // Apply top-level estimation_data fallbacks
  for (const [draftKey, edKey] of Object.entries(PREFILL_TOP_LEVEL_FALLBACKS)) {
    const draftValue = draft[draftKey];

    // Determine if the draft value is empty/missing
    let isDraftEmpty = false;
    if (draftValue === undefined || draftValue === null) {
      isDraftEmpty = true;
    } else if (typeof draftValue === 'string' && draftValue.trim() === '') {
      isDraftEmpty = true;
    } else if (typeof draftValue === 'number' && draftValue === 0) {
      isDraftEmpty = true;
    } else if (typeof draftValue === 'object' && Object.keys(draftValue as Record<string, unknown>).length === 0) {
      isDraftEmpty = true;
    }

    // Apply fallback if draft is empty
    if (isDraftEmpty) {
      const edValue = edObj[edKey];
      // Special case: permitsHidden should only use edValue if it's a boolean
      if (draftKey === 'permitsHidden') {
        if (typeof edValue === 'boolean') {
          draft[draftKey] = edValue;
        }
      } else {
        if (edValue !== undefined && edValue !== null) {
          draft[draftKey] = edValue;
        }
      }
    }
  }

  return draft;
}

/**
 * Fetch the first (oldest) quote's draft for a project.
 * Returns { quoteId, draft } on success, or null if:
 * - No quotes found
 * - Fetch fails
 * - The first quote's snapshot is unusable
 */
export async function fetchFirstProjectQuoteDraft(
  projectId: number,
): Promise<{ quoteId: number; draft: Record<string, unknown> } | null> {
  try {
    // Step 1: Fetch the list of quotes for the project (oldest first, up to 10)
    const { data: listData } = await dataProviderWithCustomMethod.getList('quotes', {
      filter: { project_id: projectId },
      sort: { field: 'created_at', order: 'ASC' },
      pagination: { page: 1, perPage: 10 },
    });
    const quoteRows = Array.isArray(listData) ? listData : [];

    if (quoteRows.length === 0) {
      return null;
    }

    // Step 2: Pick the oldest quote (client-side sort to ensure correctness)
    const firstQuote = pickFirstProjectQuote(quoteRows as ProjectQuoteListRow[]);
    if (!firstQuote) {
      return null;
    }

    // Step 3: Fetch the full quote detail to get estimation_data
    const { data: quoteDetail } = await dataProviderWithCustomMethod.getOne('quotes', {
      id: firstQuote.id,
    });
    const estimationData = (quoteDetail as Record<string, unknown>)
      .estimation_data as Record<string, unknown> | null | undefined;

    // Step 4: Extract and prepare the draft
    const draft = draftFromExistingQuoteSnapshot(estimationData);
    if (!draft) {
      return null;
    }

    return {
      quoteId: firstQuote.id,
      draft,
    };
  } catch {
    return null;
  }
}
